import { type ReactNode } from 'react';
import { Stack } from '@mui/material';
import Oreo from 'Oreo';
import { type ItemPanelState } from './StreamEditor.types';

type Props = {
    children: ReactNode;

    /*
        Set only by an entry that has a coming-soon state of its own, which is a chapter. Leave it
        out and the panel draws a plain outline.

        The thick leading edge is how this page reports that state, so it has to stay exclusive to
        the entries that have one. A summary drawn with the same edge would say 'available now'
        about a thing that is never coming soon.
    */
    state?: ItemPanelState;
};

/*
    One entry in a list of entries that each hold several fields: a chapter, a summary, a download.
    Without the outline the fields of one entry run into the fields of the next.
*/
export default function ItemPanel({ children, state }: Props) {
    return (
        <Stack
            spacing={1}
            sx={{
                border: `1px solid ${Oreo.COLOR_V3_SLATE_GREY_200}`,
                borderRadius: 1,
                padding: 2,

                ...(state && {
                    borderInlineStartWidth: 4,
                    // A palette constant, not a 'secondary.main' theme path: sx resolves those for
                    // borderColor but not for the logical borderInlineStartColor, and the invalid
                    // value is dropped without a word.
                    borderInlineStartColor:
                        state === 'coming-soon' ? Oreo.COLOR_V3_CORAL : Oreo.COLOR_V3_SLATE_GREY_200,
                }),
            }}
        >
            {children}
        </Stack>
    );
}
